import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useGetServiceLog, useServiceTypes } from "../../fetch/fetchMaintenance";
import AddEditServiceLogUI from "./addEditServiceLogUI";
import { Button } from "@mui/material";
import Spinner from "../spinner/Spinner";
import { getAuthenticatedHeaders } from "../../fetch/fetchHeaders";
import { useNotification } from "../../hooks";
import { logFeatureUsage } from "../../fetch/insightsHelper";

export default function ViewServiceLog() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { showErrorModal } = useNotification();
    const [saving, setSaving] = useState(false);

    const { data, error, isLoading } = useGetServiceLog(id);
    const [model, setModel] = useState({ engines: [] });
    const { serviceType } = model;

    const { data: serviceTypes } = useServiceTypes(model.engines.filter(e => e.impacted).map(e => e.engineSerial).join(', '));

    let inMercNet = false,
        locked = false;
    const matchingServiceTypes = serviceTypes.filter(it => it.value === serviceType);
    if (!!matchingServiceTypes && matchingServiceTypes.length > 0) {
        inMercNet = matchingServiceTypes[0].existsInMercNet;
        locked = matchingServiceTypes[0].locked;
    }

    useEffect(() => {
        logFeatureUsage('ViewServiceLog');
        document.title = 'View Service Log';
    }, []);

    const rawJson = useMemo(() => JSON.stringify(data), [data]);
    const currentJson = useMemo(() => JSON.stringify(model), [model]);
    useEffect(() => {
        if (!!data) {
            setModel({ ...data });
        }
    }, [rawJson]);

    const handleBaseFieldValueChanged = (fieldName, fieldValue) => {
        if (fieldName == 'serviceType' && fieldValue?.length > 200) {
            fieldValue = fieldValue.substring(0, 200);
        }

        setModel((prevModel) => ({
            ...prevModel,
            [fieldName]: fieldValue,
        }));
    };

    const handleEngineFieldValueChanged = (index, fieldName, fieldValue) => {
        const newModel = { ...model };

        newModel.engines[index] = { ...model.engines[index] };
        newModel.engines[index][fieldName] = fieldValue;

        if (fieldName == 'engineLocation') {
            let cityId = 0;
            let diagnosticPort = 0;
            const canBus = 'CanP';

            switch (fieldValue) {
                case 'STBD Engine':
                    cityId = 11
                    break;
                case 'PORT Engine':
                    cityId = 12
                    break;
                case 'STBD Inner Engine':
                    cityId = 13
                    break;
                case 'PORT Inner Engine':
                    cityId = 14
                    break;
                case 'STBD Outer Engine':
                    cityId = 13
                    diagnosticPort = 1;
                    break;
                case 'PORT Outer Engine':
                    cityId = 14
                    diagnosticPort = 1;
                    break;
            }
            newModel.engines[index].cityId = cityId;
            newModel.engines[index].diagnosticPort = diagnosticPort;
            newModel.engines[index].canBus = canBus;
        }

        if (fieldName == 'engineHours' && fieldValue == '') {
            newModel.engines[index][fieldName] = null;
        }

        setModel(newModel);
    };
    const handleSave = () => {
        setSaving(true);
        return getAuthenticatedHeaders({ 'Content-Type': 'application/json' }).then((headers) => {
            return fetch('/api/v1/servicelog/item', { method: 'put', headers, body: JSON.stringify(model) })
                .then(response => {
                    if (response.status < 400) {
                        navigate('/literature/service/log');
                    }
                    else {
                        showErrorModal("Failed to save service log.");
                        setSaving(false);
                    }
                });
        });
    }

    const invalidEngines = (model?.engines || []).some(engine => !engine.engineLocation || !engine.engineSerial);

    const isSaveDisabled = isLoading || !model.dateOfService || !model.serviceType || invalidEngines || rawJson === currentJson || saving;

    if (isLoading) {
        return <div style={{ position: 'relative', left: 'calc(50% - 30px)' }}><Spinner /></div>;
    }

    return (
        <AddEditServiceLogUI
            model={model}
            handleBaseFieldValueChanged={handleBaseFieldValueChanged}
            handleEngineFieldValueChanged={handleEngineFieldValueChanged}
            isEdit={model?.isEditable && !locked}
            inMercNet={inMercNet}
            locked={locked}
        >
            {model?.isEditable && !locked ? (
                <Button onClick={handleSave} disabled={isSaveDisabled && !locked}>
                    Update
                    {saving ? (
                        <>
                            &nbsp;
                            <Spinner width={16} displayInline />
                        </>
                    ) : null}
                </Button>
            ) : null}
        </AddEditServiceLogUI>
    )
}